import { StatusCodes } from 'http-status-codes';
import { ApolloError } from '@apollo/client/errors';
import type { ServerError } from '@apollo/client/link/utils';
import { CONFIG } from '@frameio/config';
import {
  GRAPHQL_ERROR_CODES,
  GRAPHQL_ERROR_MESSAGES,
} from '@frameio/graphql-client/enums';
import { VPN_CONNECTION_ERROR_MESSAGE } from './errorConstants';
import { NotAuthenticatedError, NotFoundError } from './errors';

const isError = (error: unknown): error is Error => error instanceof Error;

export const isApolloError = (error: unknown): error is ApolloError =>
  error instanceof ApolloError;

export const isInvalidSharePassphraseError = (error: unknown) => {
  return (
    isApolloError(error) &&
    error.graphQLErrors?.some((graphQLError) => {
      return (
        graphQLError?.extensions?.code ===
          GRAPHQL_ERROR_CODES.UNAUTHENTICATED &&
        graphQLError?.message ===
          GRAPHQL_ERROR_MESSAGES.INVALID_SHARE_PASSPHRASE
      );
    })
  );
};

export const getPassphraseErrorShareId = (
  error: unknown
): string | undefined => {
  if (!isApolloError(error)) return undefined;
  const passphraseError = error.graphQLErrors?.find(
    (graphQLError) =>
      graphQLError?.extensions?.code === GRAPHQL_ERROR_CODES.UNAUTHENTICATED &&
      graphQLError?.message === GRAPHQL_ERROR_MESSAGES.INVALID_SHARE_PASSPHRASE
  );
  return passphraseError?.extensions?.share_id as string | undefined;
};

export const isInvalidShareError = (error: unknown) => {
  return (
    isApolloError(error) &&
    error.graphQLErrors?.some((graphQLError) => {
      return graphQLError.message === GRAPHQL_ERROR_MESSAGES.INVALID_SHARE;
    })
  );
};

export const isNotFoundError = (error: unknown) => {
  if (!error) return false;
  return (
    (isError(error) && error instanceof NotFoundError) ||
    (isApolloError(error) &&
      error.graphQLErrors?.some((graphQLError) => {
        return (
          graphQLError.extensions?.code === GRAPHQL_ERROR_CODES.NOT_FOUND ||
          graphQLError.extensions?.http?.status === 404
        );
      }))
  );
};

/**
 * An upload link is "invalid" when the backend won't resolve the token into a
 * usable link. Either it can't find the token at all (deleted, never existed,
 * or malformed → NOT_FOUND), or it refuses access to it (expired or disabled →
 * PERMISSION_DENIED). All of these surface the same terminal "unavailable"
 * state to the anonymous uploader.
 */
export const isInvalidUploadLinkError = (error: unknown) =>
  isNotFoundError(error) || isPermissionDeniedError(error);

export const isInvalidUploadLinkPassphraseError = (error: unknown) => {
  return (
    isApolloError(error) &&
    error.graphQLErrors?.some((graphQLError) => {
      return (
        graphQLError?.extensions?.code ===
          GRAPHQL_ERROR_CODES.UNAUTHENTICATED &&
        graphQLError?.message ===
          GRAPHQL_ERROR_MESSAGES.INVALID_UPLOAD_LINK_PASSPHRASE
      );
    })
  );
};

export const isVPNConnectionErrorClientSide = (error: unknown): boolean => {
  if (!isApolloError(error)) return false;
  return Boolean(
    error.networkError &&
      (!error.graphQLErrors || !error.graphQLErrors?.length) &&
      CONFIG.requiresVpnConnection &&
      !isNotFoundError(error)
  );
};

const isServerError = (
  networkError: NonNullable<ApolloError['networkError']>
): networkError is ServerError =>
  'statusCode' in networkError && 'response' in networkError;

export const isVPNConnectionError = (error: unknown): boolean => {
  if (!isApolloError(error)) return false;
  const { networkError } = error;
  return Boolean(
    networkError &&
      isServerError(networkError) &&
      networkError.statusCode === StatusCodes.FORBIDDEN &&
      networkError.response.headers.get('Content-Type') === 'text/html' &&
      CONFIG.requiresVpnConnection
  );
};

export const isInvalidInputError = (error: unknown) => {
  if (!error) return false;
  if (isVPNConnectionError(error)) {
    return false;
  }

  return (
    /**
     * Received invalid input error from GraphQL
     */
    (isApolloError(error) &&
      error.graphQLErrors?.some((graphQLError) => {
        // e.g. message: 'Argument "assetId" has invalid value $assetId.'
        return graphQLError.message.includes('has invalid value $');
      })) ||
    /**
     * Caught invalid input before sending API request
     * @see ./paramValidators.ts
     */
    (isError(error) &&
      (error.message.startsWith('Invalid UUID') ||
        error.message.startsWith('Invalid catch all UUID')))
  );
};

export const isNotAuthenticatedError = (error: unknown) => {
  const isApolloNotAuthenticatedError =
    isApolloError(error) &&
    error.graphQLErrors?.some((graphQLError) => {
      return (
        graphQLError.extensions?.code === GRAPHQL_ERROR_CODES.UNAUTHENTICATED
      );
    });
  const isGenericNotAuthenticatedError =
    isError(error) && error instanceof NotAuthenticatedError;
  return isApolloNotAuthenticatedError || isGenericNotAuthenticatedError;
};

export const isNotAuthenticatedShareReviewerError = (error: unknown) => {
  return (
    isApolloError(error) &&
    error.graphQLErrors?.some((graphQLError) => {
      return (
        graphQLError.extensions?.code === GRAPHQL_ERROR_CODES.UNAUTHENTICATED &&
        graphQLError.extensions?.reason === 'authentication_required'
      );
    })
  );
};

export const isPermissionDeniedError = (error: unknown) => {
  if (!error) return false;
  return (
    isApolloError(error) &&
    error.graphQLErrors?.some((graphQLError) => {
      return (
        graphQLError.extensions?.code === GRAPHQL_ERROR_CODES.PERMISSION_DENIED
      );
    })
  );
};

export const isFailedPreconditionError = (error: unknown) => {
  if (!error) return false;
  return (
    isApolloError(error) &&
    error.graphQLErrors?.some((graphQLError) => {
      return (
        graphQLError.extensions?.code ===
          GRAPHQL_ERROR_CODES.FAILED_PRECONDITION &&
        graphQLError.message === 'Account locked'
      );
    })
  );
};

export const getFormattedApolloError = (
  error: unknown,
  /* Optional description of where the error was thrown from. */
  origin?: string
): ApolloError | null => {
  if (!isApolloError(error)) return null;

  const formattedOrigin = origin ? ` [${origin}]` : '';

  if (isVPNConnectionError(error)) {
    // eslint-disable-next-line no-param-reassign
    error.message = VPN_CONNECTION_ERROR_MESSAGE;
    return error;
  }

  if (error.graphQLErrors.length > 0) {
    // eslint-disable-next-line no-param-reassign
    error.message = `GraphQL API Error${formattedOrigin}: ${error.message}`;
    return error;
  }
  if (error.networkError) {
    // eslint-disable-next-line no-param-reassign
    error.message = `GraphQL API Network Error${formattedOrigin}: ${error.message}`;
    return error;
  }

  // eslint-disable-next-line no-param-reassign
  error.message = `GraphQL API Error${formattedOrigin}: ${error.message}`;
  return error;
};

export const isInvalidFilterError = (error: unknown) => {
  return (
    isApolloError(error) &&
    error.graphQLErrors?.some(
      (gqlError) =>
        gqlError?.message?.includes(`At least 1 UUID isn't a valid option`)
    )
  );
};
